import { useState } from "react";

export default function WebinarPresenterSection() {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <section className="bg-white py-14 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-alt-bg border border-gray-200 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-user-star-line text-navy text-xs" />
            <span className="text-xs font-semibold text-navy">Your Host</span>
          </div>
          <h2
            className="text-navy font-bold mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            Hear It Directly From James
          </h2>
          <p className="text-[#4a5568] text-base leading-relaxed">
            Before you register, take 30 seconds to hear from the founder himself — why he built Imperium, what you&apos;ll see in the webinar, and why it matters for your title business.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-10 items-center max-w-4xl mx-auto">
          {/* Presenter card */}
          <div className="rounded-2xl bg-alt-bg border border-gray-200 overflow-hidden">
            {/* Video area */}
            <div className="relative w-full bg-navy" style={{ aspectRatio: "16/9" }}>
              {isPlaying ? (
                <video
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  playsInline
                >
                  <source
                    src="https://storage.readdy-site.link/project_files/a5e9c8d2-9c4d-45a2-a3e3-a7805868b9ce/740cd2e9-7fca-4cf0-8d53-7476d63aebb8_webinar-preview-1.mp4?v=7058cbf43073886958dde3b215a83c48"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              ) : (
                /* Custom text-based thumbnail */
                <button
                  onClick={() => setIsPlaying(true)}
                  className="w-full h-full flex flex-col items-center justify-center cursor-pointer relative overflow-hidden group"
                  style={{ background: "linear-gradient(135deg, #0b1f33 0%, #162d47 60%, #1a3556 100%)" }}
                  aria-label="Play video message from James"
                >
                  {/* Subtle radial glow */}
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{ background: "radial-gradient(ellipse at 50% 80%, #f1663a 0%, transparent 65%)" }}
                  />
                  {/* Top label */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    <span className="text-white/80 text-[10px] font-semibold tracking-wide uppercase">Founder Message</span>
                  </div>
                  {/* Duration badge */}
                  <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm rounded-md px-2 py-0.5">
                    <span className="text-white/70 text-[10px] font-mono">&lt; 1 min</span>
                  </div>
                  {/* Center content */}
                  <div className="relative z-10 flex flex-col items-center gap-4 px-6 text-center">
                    {/* Play button */}
                    <div
                      className="w-14 h-14 rounded-full bg-primary flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ boxShadow: "0 0 0 8px rgba(241,102,58,0.15), 0 0 0 16px rgba(241,102,58,0.07)" }}
                    >
                      <i className="ri-play-fill text-white text-2xl" style={{ marginLeft: "3px" }} />
                    </div>
                    {/* Name & title */}
                    <div>
                      <p className="text-white font-bold text-base leading-tight mb-0.5">James P. Schlimmer</p>
                      <p className="text-primary text-xs font-semibold">Founder &amp; CEO, Imperium</p>
                    </div>
                    {/* Message teaser */}
                    <p className="text-white/50 text-xs leading-relaxed max-w-xs">
                      "Why I built this — and what you&apos;ll walk away with after the webinar."
                    </p>
                  </div>
                  {/* Bottom bar */}
                  <div className="absolute bottom-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
                </button>
              )}
            </div>
            <div className="px-5 md:px-7 py-5 md:py-6">
              <h3 className="text-navy font-bold text-xl mb-1">James P. Schlimmer</h3>
              <p className="text-primary font-semibold text-sm mb-4">Founder &amp; CEO, Imperium</p>
              <div className="flex flex-col gap-3">
                {[
                  { icon: "ri-building-line", text: "Founded and operated a title company from the ground up (2013)" },
                  { icon: "ri-lightbulb-flash-line", text: "Embraced technology at every layer of the title operation" },
                  { icon: "ri-robot-2-line", text: "Built Imperium to solve the outbound problem no tool could solve" },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-primary text-sm`} />
                    </span>
                    <span className="text-[#4a5568] text-sm leading-relaxed">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Founder story */}
          <div className="flex flex-col gap-6">
            <h3 className="text-navy font-bold text-xl leading-snug">
              From running a title company to building the tool that automates what manual outreach never could.
            </h3>
            <p className="text-[#4a5568] text-sm leading-relaxed" style={{ lineHeight: "1.75" }}>
              In 2013, the Imperium founder launched a title company and grew it from zero — learning firsthand every challenge the industry presents. He embraced nearly every aspect of technology into his title company to gain an edge.
            </p>
            <p className="text-[#4a5568] text-sm leading-relaxed" style={{ lineHeight: "1.75" }}>
              But there was never a way to automate outbound. The calls had to be made by a human, at the right time, to the right person — and that window of opportunity was constantly closing. Until now.
            </p>
            <div className="rounded-xl bg-navy text-white px-6 py-5">
              <p className="text-white/90 text-sm leading-relaxed italic mb-3">
                &ldquo;I&apos;ve always said, just give me a warm intro to an agent or LO and I will secure their next closing. Imperium let&apos;s you do this at hyperspeed!&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full overflow-hidden border border-white/20 flex-shrink-0">
                  <img
                    src="https://storage.readdy-site.link/project_files/a5e9c8d2-9c4d-45a2-a3e3-a7805868b9ce/b5e8d2e5-3773-4316-9331-289c9ccf9788_MDP_5507_EDIT2.png?v=8ce0c3518112be2bf32e637bb0d8021a"
                    alt="James P. Schlimmer"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <p className="text-white/60 text-xs">— James P. Schlimmer, CEO of Imperium</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="flex flex-col gap-3 pt-2">
              {[
                { year: "2013", label: "Founded title company from the ground up" },
                { year: "2015+", label: "Embraced technology — CRM, automation, digital ops" },
                { year: "2023", label: "Identified the missing piece: real-time AI outbound" },
                { year: "2024", label: "Imperium launched — active in all 50 states" },
              ].map((item) => (
                <div key={item.year} className="flex items-center gap-4">
                  <div className="w-14 text-right">
                    <span className="text-xs font-bold text-primary">{item.year}</span>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  <span className="text-[#4a5568] text-sm leading-relaxed">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
